import { format } from "date-fns";

import useSchedule from "./useSchedule";
import useTranslations from "./useTranslations";
import useDateFnsLocale from "./useDateFnsLocale";
import useFormatDate from "./useFormatDate";

const useScheduleAsWords = (options: {
  schedule?: string;
  endDate?: Date;
  startDate?: Date;
}): string => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  const startDate = useFormatDate({ date: options.startDate });
  const endDate = useFormatDate({ date: options.endDate });
  const { scheduleType, dayOfMonthOption, dayOfWeek, daysOfMonth, months } =
    useSchedule({ schedule: options.schedule, hasEndDate: !!options.endDate });

  if (!scheduleType) {
    return null;
  }

  switch (scheduleType) {
    case "weeks":
      return t(`reviews.schedule.weeks`, {
        dayOfWeek: dateFnsLocale.localize.day(dayOfWeek),
      }) as string;
    case "months":
      return t(`reviews.schedule.months.${dayOfMonthOption}` as const, {
        scheduleCount: daysOfMonth?.length,
        daysOfMonth: daysOfMonth?.join(" / "),
      }) as string;
    case "someMonths":
      return t(`reviews.schedule.someMonths.${dayOfMonthOption}` as const, {
        monthsCount: months.length,
        months: months
          .map((month) => dateFnsLocale.localize.month(Number(month) - 1))
          .join(" / "),
        daysCount: daysOfMonth?.length,
        days: daysOfMonth?.join(" / "),
      }) as string;
    case "startAndFinish":
      return t("reviews.schedule.startAndFinish", {
        start: startDate,
        end: endDate,
      }) as string;
    case "onetime":
      return t("reviews.schedule.onetime", {
        start: startDate,
      }) as string;
    default:
      return t("reviews.schedule.invalid") as string;
  }
};

export default useScheduleAsWords;
